import { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HistoryAssessment } from '../examiner/exam-sections/HistoryAssessment';
import type { PersonInput } from '@/lib/assessment/namatAssessment';
import { PhysicalAssessment } from '../examiner/exam-sections/PhysicalAssessment';
import { EyeAssessment } from '../examiner/exam-sections/EyeAssessment';
import { HearingAssessment } from '../examiner/exam-sections/HearingAssessment';
import { DentalAssessment } from '../examiner/exam-sections/DentalAssessment';
import { useLanguage } from '@/contexts/LanguageContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { calculateSectionScore } from '../examiner/utils/examScoring';
import type { ExamFormData } from '../examiner/utils/examValidation';

interface CoachExamTabsProps {
  form: UseFormReturn<ExamFormData>;
  /** Birth date and sex for the indicators (#90, #91). */
  person?: PersonInput;
}

interface ExamTab {
  id: string;
  enabled: boolean;
}

const defaultTabs: ExamTab[] = [
  { id: 'history', enabled: true },
  { id: 'physical', enabled: true },
  { id: 'eye', enabled: true },
  { id: 'hearing', enabled: true },
  { id: 'dental', enabled: true },
];

export function CoachExamTabs({ form, person }: CoachExamTabsProps) {
  const [tabs, setTabs] = useState<ExamTab[]>(defaultTabs);
  const [loading, setLoading] = useState(true);
  const { t, dir } = useLanguage();
  
  // Watch form values to recalculate scores
  const formValues = form.watch();

  useEffect(() => {
    const fetchTabs = async () => {
      try {
        const tabsDoc = await getDoc(doc(db, 'settings', 'examTabs'));
        if (tabsDoc.exists()) {
          setTabs(tabsDoc.data().tabs);
        }
      } catch (error) {
        console.error('Error fetching exam tabs for coach:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTabs();
  }, []);

  const enabledTabs = tabs.filter(tab => tab.enabled);
  const defaultTab = enabledTabs[0]?.id || 'history';

  // Function to get score for a section
  const getSectionScore = (sectionId: string): number => {
    const scorableSections = ['history', 'physical'];
    if (!scorableSections.includes(sectionId)) return 0;
    return calculateSectionScore(sectionId, formValues);
  };

  // Calculate total score based on all scorable sections
  const getTotalScore = (): number => {
    const scorableSections = ['history', 'physical'];
    const scores = scorableSections.map(section => getSectionScore(section));
    const validScores = scores.filter(score => score > 0);
    
    if (validScores.length === 0) return 0;
    
    const totalScore = validScores.reduce((sum, score) => sum + score, 0);
    return Math.round(totalScore / validScores.length);
  };

  if (loading) {
    return null;
  }

  return (
    <Tabs defaultValue={defaultTab} className="w-full" dir={dir}>
      <TabsList className="flex w-full h-auto p-1 flex-wrap justify-between gap-1">
        <div className="flex flex-wrap gap-1">
          {tabs.map((tab) => (
            tab.enabled && (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id} 
                className="text-xs px-2 py-1.5 flex-shrink-0 whitespace-nowrap min-w-fit border border-gray-300 data-[state=active]:border-transparent"
              >
                <div className="flex items-center gap-1">
                  <span>{t(`dashboard.coach.examination.sections.${tab.id}`)}</span>
                  <span className="text-[#7dda55] font-semibold text-xs">
                    {getSectionScore(tab.id)}%
                  </span>
                </div>
              </TabsTrigger>
            )
          ))}
        </div>
        <div className="flex items-center px-3 py-1.5 bg-[#7dda55] text-white rounded-md font-semibold text-sm ml-2">
          <span>{t('dashboard.coach.examination.totalScore')}: {getTotalScore()}%</span>
        </div>
      </TabsList>

      <div className="mt-4 p-4 bg-muted/50 rounded-lg">
        {tabs.map((tab) => (
          tab.enabled && (
            <TabsContent key={tab.id} value={tab.id}>
              {tab.id === 'history' && <HistoryAssessment control={form.control} />}
              {tab.id === 'physical' && <PhysicalAssessment control={form.control} setValue={form.setValue} person={person} />}
              {tab.id === 'eye' && <EyeAssessment control={form.control} />}
              {tab.id === 'hearing' && <HearingAssessment control={form.control} />}
              {tab.id === 'dental' && <DentalAssessment control={form.control} />}
            </TabsContent>
          )
        ))}
      </div>
    </Tabs>
  );
}
